import '../../@types';

const waitLoaded: { [k: string]: null | undefined | true | any } = {};

/**
 * 检查是否已加载完成，通过 checkLoaded 来判断是否已加载，成功返回 true 或其它对象，失败（比如网络异常，系统错误，超时等）则返回 false
 * @parm name 需要等待加载的对象名称，用于记录加载状态，起到放并发的控制效果，防止重复加载
 * @param checkLoaded 检查函数，返回 false 表示显示表示加载失败，没有返回或返回 null|undefined 表示还未加载完成，其它值表示加载成功
 * @param timeout 等价加载的超时时间，单位毫秒
 */
export async function waitLoading<T extends boolean | any>(name: string, checkLoaded: () => T, timeout: number = 5000): Promise<T> {
    if (waitLoaded[name]) return waitLoaded[name]; // 已加载
    if (waitLoaded[name] == null) {
        waitLoaded[name] = false; // 首次加载
        return false as T;
    }
    const start = Date.now();
    console.debug(`[waitLoading] ${name} 等待加载`);
    return new Promise((resolve) => {
        function clearTimer() {
            if (timer) {
                clearInterval(timer);
                timer = undefined as any;
            }
        }
        let timer = setInterval(() => {
            const now = Date.now();
            const ret = checkLoaded();
            if (ret) {
                // 加载成功
                clearTimer();
                console.info(`[waitLoading] ${name} 加载成功: waited ${now - start}ms, timeout ${timeout}ms`);
                waitLoaded[name] = ret;
                resolve(ret);
            } else if (ret === false) {
                clearTimer();
                console.warn(`[waitLoading] ${name} 加载失败: waited ${now - start}ms, timeout ${timeout}ms`);
                delete waitLoaded[name];
                resolve(false as T);
            } else if (now - start >= timeout) {
                clearTimer();
                console.warn(`[waitLoading] ${name} 等待超时: waited ${now - start}ms, timeout ${timeout}ms`);
                delete waitLoaded[name];
                resolve(false as T);
                return;
            }
        }, 50);
    });
}

/** 创建指定 HTML DOM 元素 */
export function createElement<T extends keyof HTMLElementTagNameMap>(
    elem: T,
    attrs?: Partial<{ [k in keyof HTMLElementTagNameMap[T]]: Partial<HTMLElementTagNameMap[T][k]> }>,
    content?: string,
    parent?: HTMLElement,
): HTMLElementTagNameMap[T] {
    const dom = document.createElement(elem);
    // if (attrs) for (const k of Object.keys(attrs)) dom.setAttribute(k, attrs[k] as any);
    if (attrs) for (const k of Object.keys(attrs)) dom[k] = attrs[k];
    dom.innerHTML = content || '';
    parent?.appendChild(dom);
    return dom;
}

/** 根据字符创创建 HTML DOM 元素 */
export function createElementByHtmlString(html: string, parent?: HTMLElement): DocumentFragment {
    const div = document.createElement('div');
    const fragment = document.createDocumentFragment();
    div.innerHTML = html;
    fragment.append(...Array.from(div.childNodes));
    // const children = Array.from(div.children);
    // for (const c of children) fragment.appendChild(c);
    if (parent) parent.appendChild(fragment);
    return fragment;
}

/** 使用 script 标签加载脚本 */
export async function loadScript(src: string, onload?: () => void, attrs?: Partial<{ [k in keyof HTMLScriptElement]: any }>): Promise<void> {
    try {
        const script = createElement('script', Object.assign(attrs || {}, { type: 'text/javascript', src }));
        return new Promise((resolve, reject) => {
            script.addEventListener('load', (ev) => {
                console.debug(`[Act][loadScript] ${script.src} 加载成功`);
                onload?.();
                resolve(void 0);
            });
            script.addEventListener('error', (ev) => {
                console.error(`[Act][loadScript] ${script.src} 加载失败：`, ev);
                reject(ev.error);
            });
            document.body.appendChild(script);
        });
    } catch (err) {
        console.error(`[Act][loadScript] ${src} 加载失败：`, err);
        return;
    }
}

/** 使用 link 标签加载样式文件 */
export function loadStyleByUrl(href: string, onload?: () => void, attrs?: object): Promise<void> {
    const link = createElement('link', Object.assign(attrs || {}, { rel: 'stylesheet', type: 'text/css', href }));
    return new Promise((resolve, reject) => {
        link.addEventListener('load', (ev) => {
            console.debug(`[Act][loadStyle] ${link.href} 加载成功`);
            onload?.();
            resolve(void 0);
        });
        link.addEventListener('error', (ev) => {
            console.error(`[Act][loadStyle] ${link.href} 加载失败：`, ev);
            reject(ev.error);
        });
        document.head.appendChild(link);
    });
}

/** 使用 style 标签动态加载样式 */
export function loadStyle(content: string) {
    createElement('style', { type: 'text/css' }, content, document.head);
}
